import type { Exercise, ExerciseKind } from "@repo/hindi-shared";
import {
  letterById,
  lessonById,
  lessonList,
  mulberry32,
  taughtItemIds,
} from "@repo/hindi-shared";

import { dateKey, hashSeed } from "../exercises/seed";
import { navigate } from "../router";
import { setPendingPlan } from "../session/customPlan";
import { loadState } from "../state/store";
import { DualLabel } from "../ui/DualLabel";
import { PaperPanel } from "../ui/PaperPanel";
import { Shirorekha } from "../ui/Shirorekha";

const DRILL_SIZE = 10;

interface DrillDef {
  kind: ExerciseKind;
  titleEn: string;
  titleHi: string;
  descriptionEn: string;
  descriptionHi: string;
}

/** One tile per exercise kind launchable as a standalone single-kind drill,
 * in teaching order. `paper-dictation` is excluded — it self-manages a
 * whole block (intro card, per-item dictation, reveal) via `PaperDictation`
 * rather than rendering one exercise per `ExerciseHost` mount, so it isn't
 * launchable through the drill screen's single-block "review" plan; it's
 * listed separately below as a "coming soon" tile for that structural
 * reason, not because it lacks a component. */
const IMPLEMENTED_DRILLS: DrillDef[] = [
  {
    kind: "letter-intro",
    titleEn: "Letter intro",
    titleHi: "अक्षर परिचय",
    descriptionEn: "First meeting with a new letter",
    descriptionHi: "नए अक्षर से पहली मुलाक़ात",
  },
  {
    kind: "sound-to-symbol",
    titleEn: "Listen and choose",
    titleHi: "सुनो और चुनो",
    descriptionEn: "Hear the sound, pick the right letter",
    descriptionHi: "आवाज़ सुनकर सही अक्षर चुनें",
  },
  {
    kind: "symbol-to-sound",
    titleEn: "Name the letter",
    titleHi: "अक्षर पहचानो",
    descriptionEn: "See the letter, pick the right sound",
    descriptionHi: "अक्षर देखकर सही आवाज़ चुनें",
  },
  {
    kind: "barakhadi",
    titleEn: "Barakhadi",
    titleHi: "बाराखड़ी",
    descriptionEn: "Practice consonant + vowel-sign combinations",
    descriptionHi: "व्यंजन और मात्रा के मेल का अभ्यास",
  },
  {
    kind: "word-reading",
    titleEn: "Word reading",
    titleHi: "शब्द पढ़ना",
    descriptionEn: "Practice reading whole words",
    descriptionHi: "पूरे शब्द को पढ़ने का अभ्यास",
  },
  {
    kind: "word-builder",
    titleEn: "Word builder",
    titleHi: "शब्द बनाओ",
    descriptionEn: "Compose a word from letter tiles",
    descriptionHi: "अक्षर जोड़कर शब्द बनाएँ",
  },
  {
    kind: "sentence-reading",
    titleEn: "Sentence reading",
    titleHi: "वाक्य पढ़ना",
    descriptionEn: "Practice reading whole sentences",
    descriptionHi: "पूरे वाक्य पढ़ने का अभ्यास",
  },
];

const COMING_SOON_DRILLS: DrillDef[] = [
  {
    kind: "paper-dictation",
    titleEn: "Paper dictation",
    titleHi: "कागज़ पर लिखो",
    descriptionEn: "Practice writing on paper from dictation",
    descriptionHi: "सुनकर कागज़ पर लिखने का अभ्यास",
  },
];

/** All item ids taught by the learner's most advanced completed lesson (or none, if no lesson is complete yet). */
function taughtByProgress(completedLessonIds: readonly string[]): Set<string> {
  const completed = new Set(completedLessonIds);
  let latest: string | null = null;
  for (const lesson of lessonList) {
    if (completed.has(lesson.id)) latest = lesson.id;
  }
  return latest === null ? new Set<string>() : taughtItemIds(latest);
}

/** Union of word ids unlocked by every completed lesson. */
function unlockedWordIds(completedLessonIds: readonly string[]): string[] {
  const ids = new Set<string>();
  for (const lessonId of completedLessonIds) {
    const lesson = lessonById.get(lessonId);
    if (lesson === undefined) continue;
    for (const wordId of lesson.wordIds) ids.add(wordId);
  }
  return [...ids];
}

/** Union of sentence ids unlocked by every completed lesson. */
function unlockedSentenceIds(completedLessonIds: readonly string[]): string[] {
  const ids = new Set<string>();
  for (const lessonId of completedLessonIds) {
    const lesson = lessonById.get(lessonId);
    if (lesson === undefined) continue;
    for (const sentenceId of lesson.sentenceIds ?? []) ids.add(sentenceId);
  }
  return [...ids];
}

/** The pool of item ids a given drill kind can practice with, given the learner's current unlocks. */
function poolFor(
  kind: ExerciseKind,
  taught: Set<string>,
  wordIds: string[],
  sentenceIds: string[],
): string[] {
  switch (kind) {
    case "letter-intro":
    case "sound-to-symbol":
    case "symbol-to-sound":
      return [...taught];
    case "barakhadi":
      return [...taught].filter((id) => letterById.get(id)?.kind === "matra");
    case "word-reading":
    case "word-builder":
      return wordIds;
    case "sentence-reading":
      return sentenceIds;
    case "paper-dictation":
      return [];
    default:
      return [];
  }
}

/** Seeded, deterministic-for-the-day sample of up to `count` ids from `pool` (Fisher-Yates, no replacement). */
function sampleForToday(pool: readonly string[], count: number): string[] {
  const rand = mulberry32(hashSeed(`drill:${dateKey()}`));
  const copy = [...pool];
  for (let i = copy.length - 1; i > 0; i -= 1) {
    const j = Math.floor(rand() * (i + 1));
    const tmp = copy[i]!;
    copy[i] = copy[j]!;
    copy[j] = tmp;
  }
  return copy.slice(0, count);
}

/**
 * Drills — a grid of one PaperPanel per exercise kind. Tapping an available
 * kind launches a 10-exercise single-kind session (a custom `SessionPlan`
 * with one block) via the same `SessionScreen`/runner every other practice
 * uses. Kinds with no content available yet for this learner show "Not
 * yet"; kinds with no real component at all yet show "Coming soon".
 */
export function Drills() {
  const store = loadState();
  const taught = taughtByProgress(store.completedLessonIds);
  const wordIds = unlockedWordIds(store.completedLessonIds);
  const sentenceIds = unlockedSentenceIds(store.completedLessonIds);

  function handleLaunch(kind: ExerciseKind): void {
    const pool = poolFor(kind, taught, wordIds, sentenceIds);
    if (pool.length === 0) return;
    const itemIds = sampleForToday(pool, DRILL_SIZE);
    const exercises: Exercise[] = itemIds.map((itemId) => ({ kind, itemId }));
    setPendingPlan({
      newLessonId: null,
      blocks: [
        { kind: "review", exercises },
        { kind: "summary", exercises: [] },
      ],
    });
    navigate("/session");
  }

  // Nothing unlocked at all — a brand-new learner wandered in before
  // finishing a first lesson. Explain how drills open, warmly and plainly.
  const nothingAvailable =
    taught.size === 0 && wordIds.length === 0 && sentenceIds.length === 0;

  return (
    <main className="paper-grain min-h-dvh px-6 py-12">
      <section className="mx-auto w-full max-w-2xl">
        <Shirorekha className="pt-4">
          <button
            type="button"
            onClick={() => navigate("/")}
            aria-label="Home"
            className="inline-flex items-center gap-1.5 transition-colors duration-150 hover:text-sindoor"
          >
            <span aria-hidden="true" className="text-xs text-ink-soft">
              ←
            </span>
            <DualLabel
              en="Hindi"
              hi="हिन्दी"
              className="text-xs text-ink-soft uppercase"
            />
          </button>
          <h1 className="mt-4 font-display text-5xl leading-[0.95] text-ink">
            <DualLabel en="Drills" hi="अभ्यास" />
          </h1>
          {nothingAvailable && (
            <p className="mt-2 font-latin text-base text-ink-soft">
              <span lang="en">
                Drills open after your first lesson. Finish today&apos;s lesson,
                then come back here.
              </span>
            </p>
          )}
        </Shirorekha>

        <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2">
          {IMPLEMENTED_DRILLS.map((drill) => {
            const pool = poolFor(drill.kind, taught, wordIds, sentenceIds);
            const available = pool.length > 0;
            return (
              <button
                key={drill.kind}
                type="button"
                disabled={!available}
                onClick={() => handleLaunch(drill.kind)}
                className="group text-left disabled:cursor-not-allowed disabled:opacity-50"
              >
                <PaperPanel className="h-full transition-colors duration-150 group-enabled:group-hover:border-ink">
                  <p className="font-dev text-xl text-ink transition-colors duration-150 group-enabled:group-hover:text-sindoor">
                    <DualLabel en={drill.titleEn} hi={drill.titleHi} />
                  </p>
                  <p className="mt-2 font-latin text-sm text-ink-soft">
                    <span lang="en">{drill.descriptionEn}</span>
                  </p>
                  {!available && (
                    <p className="mt-3 font-dev text-xs text-sindoor uppercase">
                      <span lang="en">Not yet</span>
                    </p>
                  )}
                </PaperPanel>
              </button>
            );
          })}

          {COMING_SOON_DRILLS.map((drill) => (
            <div key={drill.kind} className="opacity-50">
              <PaperPanel className="h-full">
                <p className="font-dev text-xl text-ink">
                  <DualLabel en={drill.titleEn} hi={drill.titleHi} />
                </p>
                <p className="mt-2 font-latin text-sm text-ink-soft">
                  <span lang="en">{drill.descriptionEn}</span>
                </p>
                <p className="mt-3 font-dev text-xs text-sindoor uppercase">
                  <span lang="en">Coming soon</span>
                </p>
              </PaperPanel>
            </div>
          ))}
        </div>
      </section>
    </main>
  );
}
